// src/components/settings/UserManagement.js
import React, { useState } from "react";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Avatar,
  Tooltip,
  Chip,
  Paper,
  Stack,
  useTheme,
  TablePagination,
  useMediaQuery,
  Button,
  Dialog,
  DialogContent,
} from "@mui/material";
import {
  Edit,
  Delete,
  Search,
  Refresh,
  Add,
  Circle, // ← AÑADIR
} from "@mui/icons-material";
import { useCollection } from "../../hooks/useCollection";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import LoadingSpinner from "../ui/LoadingSpinner";
import EmptyState from "../ui/EmptyState";
import ConfirmDialog from "../ui/ConfirmDialog";
import UserForm from "./UserForm";

// Función helper para formatear fechas relativas
const formatLastSeen = (dateString) => {
  if (!dateString) return "Nunca";
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  if (diffSeconds < 60) return "Ahora mismo";
  if (diffMinutes < 60) return `Hace ${diffMinutes} min`;
  if (diffHours < 24) return `Hace ${diffHours}h`;
  if (diffDays === 1) return "Ayer";
  if (diffDays < 7) return `Hace ${diffDays} días`;
  if (diffWeeks < 4) return `Hace ${diffWeeks} semanas`;
  if (diffMonths < 12) return `Hace ${diffMonths} meses`;
  
  return date.toLocaleDateString("es-ES", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// Función para obtener el color del indicador de actividad
const getActivityStatus = (user) => {
  if (!user.isActive) return { color: "error", label: "Inactivo" };
  if (!user.lastLogin) return { color: "warning", label: "Sin actividad" };
  
  const lastLogin = new Date(user.lastLogin);
  const now = new Date();
  const diffHours = (now - lastLogin) / (1000 * 60 * 60);
  
  if (diffHours < 1) return { color: "success", label: "En línea" };
  if (diffHours < 24) return { color: "success", label: "Activo hoy" };
  if (diffHours < 48) return { color: "info", label: "Activo ayer" };
  if (diffHours < 168) return { color: "info", label: "Activo esta semana" };
  if (diffHours < 720) return { color: "warning", label: "Activo este mes" };
  return { color: "error", label: "Inactivo" };
};

const UserManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: users, loading } = useCollection("users");
  const { data: roles } = useCollection("roles");
  const [localUsers, setLocalUsers] = useState(null);
  const { remove } = useAuditFirestore("users");
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  React.useEffect(() => {
    if (users) {
      setLocalUsers(users);
    }
  }, [users]);

  React.useEffect(() => {
    setPage(0);
  }, [search]);

  const getRoleName = (roleId) => {
    if (!roleId) return "Sin rol";
    const role = roles?.find(r => r.id === roleId);
    return role?.name || roleId;
  };

  const filtered = localUsers?.filter(
    (u) =>
      u.isActive !== false &&
      (search === "" ||
        u.displayName?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase()) ||
        getRoleName(u.role).toLowerCase().includes(search.toLowerCase()))
  );

  const paginatedUsers = filtered?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setShowForm(true);
  };

  const handleSave = () => {
    setShowForm(false);
    setEditingUser(null);
    if (users) setLocalUsers(users);
  };

  if (loading || !localUsers) return <LoadingSpinner />;

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: { xs: 2, sm: 3 },
          borderRadius: { xs: "16px", sm: "20px" },
          border: "1px solid",
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.12)"
              : "#e2e8f0",
          background: theme.palette.background.paper,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          gap={1.5}
          flexWrap="wrap"
        >
          <TextField
            placeholder="Buscar por nombre, email o rol"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              width: { xs: "100%", sm: 300, md: 360 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                background: theme.palette.background.paper,
                height: 44,
                display: "flex",
                alignItems: "center",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search
                    fontSize="small"
                    sx={{ color: theme.palette.primary.main }}
                  />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}>
                    ✖
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={`Total: ${filtered?.length || 0}`}
              sx={{
                height: 44,
                borderRadius: "12px",
                fontWeight: 700,
                fontSize: { xs: "0.75rem", sm: "0.85rem" },
                background: theme.palette.background.paper,
                border: "1px solid",
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
              }}
            />
            <Tooltip title="Refrescar">
              <IconButton
                onClick={() => {
                  if (users) setLocalUsers(users);
                }}
                size="small"
                sx={{
                  color: theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(0, 175, 163, 0.1)"
                        : "rgba(0, 95, 101, 0.05)",
                  },
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setEditingUser(null);
                setShowForm(true);
              }}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 600,
                height: 44,
                boxShadow: "none",
                fontSize: { xs: "0.8rem", sm: "0.85rem" },
                "&:hover": {
                  boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
                  filter: "brightness(1.05)",
                },
              }}
            >
              Nuevo Usuario
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {filtered?.length === 0 ? (
        <EmptyState
          title={search ? "Sin resultados" : "Sin usuarios"}
          description={
            search ? "Prueba con otros términos" : "Añade el primer usuario"
          }
          actionLabel="Nuevo Usuario"
          onAction={() => {
            setEditingUser(null);
            setShowForm(true);
          }}
        />
      ) : (
        <Card
          elevation={2}
          sx={{
            borderRadius: { xs: 1, sm: 2 },
            overflow: "hidden",
            border: "1px solid",
            borderColor: theme.palette.divider,
            background: theme.palette.background.paper,
          }}
        >
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(0, 175, 163, 0.1)"
                        : "rgba(0, 95, 101, 0.05)",
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      width: { xs: 50, sm: 60 },
                      color: theme.palette.primary.main,
                      fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    }}
                  >
                    {isMobile ? "" : "Avatar"}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                      fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    }}
                  >
                    Usuario
                  </TableCell>
                  {!isMobile && (
                    <>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.primary.main,
                          fontSize: "0.85rem",
                        }}
                      >
                        Email
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.primary.main,
                          fontSize: "0.85rem",
                        }}
                      >
                        Rol
                      </TableCell>
                    </>
                  )}
                  {/* NUEVA COLUMNA: Estado de actividad */}
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                      fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    }}
                  >
                    Actividad
                  </TableCell>
                  {/* NUEVA COLUMNA: Última conexión */}
                  {!isMobile && (
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        fontSize: "0.85rem",
                      }}
                    >
                      Última conexión
                    </TableCell>
                  )}
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      width: { xs: 100, sm: 130 },
                      color: theme.palette.primary.main,
                      fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    }}
                  >
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedUsers?.map((user) => {
                  const activityStatus = getActivityStatus(user);
                  const lastSeen = formatLastSeen(user.lastLogin);
                  
                  return (
                    <TableRow
                      key={user.id}
                      hover
                      sx={{
                        "&:hover": {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.03)"
                              : "rgba(0, 95, 101, 0.02)",
                        },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ position: "relative", display: "inline-flex" }}>
                          <Avatar
                            sx={{
                              width: { xs: 32, sm: 40 },
                              height: { xs: 32, sm: 40 },
                              background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                              fontSize: { xs: "0.8rem", sm: "1rem" },
                              fontWeight: 500,
                            }}
                          >
                            {(user.displayName || user.email || "U")
                              ?.charAt(0)
                              ?.toUpperCase()}
                          </Avatar>
                          {/* Indicador de actividad (puntito verde/rojo) */}
                          <Circle
                            sx={{
                              position: "absolute",
                              bottom: -2,
                              right: -2,
                              fontSize: 12,
                              color: `${activityStatus.color}.main`,
                              filter: "drop-shadow(0 0 2px rgba(0,0,0,0.3))",
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                        >
                          {user.displayName || "Sin nombre"}
                        </Typography>
                        {isMobile && (
                          <>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.65rem" }}
                            >
                              {user.email}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: "0.65rem", display: "block" }}
                            >
                              Último acceso: {lastSeen}
                            </Typography>
                          </>
                        )}
                      </TableCell>
                      {!isMobile && (
                        <>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "0.85rem" }}
                            >
                              {user.email || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getRoleName(user.role)}
                              size="small"
                              sx={{
                                background: `linear-gradient(135deg, ${theme.palette.primary.main}20, ${theme.palette.primary.main}10)`,
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                                fontSize: { xs: "0.7rem", sm: "0.75rem" },
                              }}
                            />
                          </TableCell>
                        </>
                      )}
                      {/* Celda de Actividad */}
                      <TableCell>
                        <Tooltip title={activityStatus.label}>
                          <Chip
                            icon={
                              <Circle
                                sx={{
                                  fontSize: "8px !important",
                                  color: `${activityStatus.color}.main !important`,
                                }}
                              />
                            }
                            label={activityStatus.label}
                            size="small"
                            color={activityStatus.color}
                            variant="outlined"
                            sx={{
                              fontSize: { xs: "0.65rem", sm: "0.7rem" },
                              height: { xs: 22, sm: 24 },
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                      {/* Celda de Última conexión */}
                      {!isMobile && (
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ 
                              fontSize: "0.8rem",
                              color: activityStatus.color === "error" 
                                ? "error.main" 
                                : "text.secondary"
                            }}
                          >
                            {lastSeen}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="flex-end"
                        >
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(user);
                              }}
                              sx={{ color: theme.palette.primary.main }}
                            >
                              <Edit sx={{ fontSize: { xs: 18, sm: 20 } }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteTarget(user);
                              }}
                              sx={{ color: theme.palette.error.main }}
                            >
                              <Delete sx={{ fontSize: { xs: 18, sm: 20 } }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filtered?.length || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage={isMobile ? "Filas:" : "Filas por página:"}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
            sx={{
              borderTop: "1px solid",
              borderColor: theme.palette.divider,
              "& .MuiTablePagination-toolbar": {
                minHeight: 48,
                px: { xs: 1, sm: 2 },
                fontSize: { xs: "0.75rem", sm: "0.85rem" },
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                {
                  fontSize: { xs: "0.7rem", sm: "0.85rem" },
                },
            }}
          />
        </Card>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          setLocalUsers((prev) => prev.filter((u) => u.id !== deleteTarget.id));
          const deletedUser = deleteTarget;
          setDeleteTarget(null);

          try {
            await remove(deletedUser.id);
          } catch (error) {
            console.error("Error deleting:", error);
            setLocalUsers((prev) => [...prev, deletedUser]);
          }
        }}
        title="Eliminar Usuario"
        message={`¿Eliminar a ${deleteTarget?.displayName || deleteTarget?.email || "este usuario"}?`}
        type="delete"
      />

      <Dialog
        key={editingUser?.id || "new"}
        open={showForm}
        onClose={() => {
          setShowForm(false);
          setEditingUser(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "24px",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <UserForm
            user={editingUser}
            onSave={handleSave}
            onCancel={() => {
              setShowForm(false);
              setEditingUser(null);
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default UserManagement;